<template>
  <div class="top-recognisers-table">
    <h6 class="top-recognisers-table__title">Top recognisers</h6>
    <b-spinner v-if="loading" small />
    <template v-else-if="recognisers.length > 0">
      <b-table :items="recognisers" :fields="fields" hover responsive borderless class="top-recognisers-table__table">
        <template #cell(name)="{ item }">
          <div class="top-recognisers-table__name-cell">
            <div class="top-recognisers-table__avatar">
              <img v-if="item.avatar_url" :src="item.avatar_url" :alt="item.full_name" />
              <span v-else class="top-recognisers-table__initials">{{ getInitials(item.full_name) }}</span>
            </div>
            <div class="top-recognisers-table__name">
              <span>{{ item.full_name }}</span>
              <b-badge v-if="item.is_portal_admin" pill variant="light" class="top-recognisers-table__admin-badge">
                Portal admin
              </b-badge>
            </div>
          </div>
        </template>

        <template #cell(sent_count)="{ item }">
          <span>{{ item.sent_count }}</span>
        </template>
      </b-table>
    </template>
    <div v-else class="top-recognisers-table__empty">
      <p class="text-muted mb-0">No data available.</p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { getInitials } from '@/utils';

defineProps({
  recognisers: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const fields = computed(() => [
  { key: 'name', label: 'Name', thClass: 'top-recognisers-table__th' },
  {
    key: 'sent_count',
    label: 'Sent',
    thClass: 'top-recognisers-table__th top-recognisers-table__th--right',
    tdClass: 'text-end',
  },
]);
</script>

<style scoped lang="scss">
.top-recognisers-table {
  background-color: var(--palette-color-base-white);
  border-radius: 0.25rem;
  box-shadow: var(--depth-2);
  padding: 1.25rem 1.5rem;
  height: 100%;

  &__title {
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--palette-color-default);
  }

  &__table {
    margin-bottom: 0;

    :deep(th) {
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      color: var(--palette-color-default-lighten-30);
      border-bottom: 1px solid var(--palette-color-default-lighten-80);
      padding: 0.5rem 0.75rem;
    }

    :deep(td) {
      font-size: 0.875rem;
      color: var(--palette-color-default);
      padding: 0.75rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tr:last-child td) {
      border-bottom: none;
    }
  }

  &__th--right {
    text-align: right;
  }

  &__name-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__avatar {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: var(--palette-color-core-1-lighten-80);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__initials {
    font-weight: 600;
    color: var(--palette-color-brand-primary);
  }

  // Stacked, not inline: beside a long email the tag pushes the Sent column out of the card.
  &__name {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  &__admin-badge {
    background-color: var(--palette-color-default-lighten-90);
    color: var(--palette-color-default);
    font-weight: 400;
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 6.25rem;
  }
}
</style>
